import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OCC_CART_ID_CURRENT, OCC_USER_ID_ANONYMOUS, RoutingService } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, skip, switchMap, take, tap } from 'rxjs/operators';
import { ActiveCartFacade } from '../../../../features/cart/base';
import { CartActions } from '../../../../features/cart/base/store/actions';
import { OrderProcessingConnector } from '../../../../features/checkout/base/connector';
import { OrderDetailsConnector } from '../../../../features/order-base/connector/order-details-connector';
import { SoldToActions } from '../../../../features/sold-to-base/store/actions';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { GlobalMessageActions } from '../../../global-message/store';
import {
  B2BComment,
  CartSimulation,
  CartType,
  ErrorModel,
  FeedBadgeType,
  Order,
  OrderApproval,
  OrderApprovalDecisionValue,
  OrderApprovalSearchResult,
  OrderDetails,
  OrderProcessStatus,
  OrderStatus,
  OrderStatusTabType,
} from '../../../model';
import { normalizeHttpError } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { OrderApprovalConnector } from '../../connectors/order-approval.connector';
import { OrderApprovalFacade } from '../../facades';
import { UserActions } from '../actions';

@Injectable()
export class OrderApprovalEffects {
  loadOrderApprovalSimulation$: Observable<
    UserActions.LoadOrderApprovalSimulationSuccess | UserActions.LoadOrderApprovalSimulationFail | SoldToActions.LoadActiveSoldTo
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVAL_SIMULATION),
      map((action: UserActions.LoadOrderApprovalSimulation) => action.payload),
      filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
      switchMap(({ userId, orderApprovalCode }) => {
        return this.orderApprovalConnector.getPending(userId, orderApprovalCode).pipe(
          switchMap((simulation: CartSimulation) => {
            return [
              new UserActions.LoadOrderApprovalSimulationSuccess(simulation, orderApprovalCode),
              new SoldToActions.LoadActiveSoldTo(userId),
            ];
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new UserActions.LoadOrderApprovalSimulationFail({
                orderApprovalCode,
                error: normalizeHttpError(error),
              })
            )
          )
        );
      })
    )
  );

  handleFailureForOrderApprovalSimulation$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVAL_SIMULATION_FAIL),
        map((action: UserActions.LoadOrderApprovalSimulationFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderApproval.loadOrderApprovalSimulationFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  loadApprovedOrderApprovalDetails$: Observable<
    | UserActions.LoadApprovedOrderApprovalDetailsSuccess
    | UserActions.LoadApprovedOrderApprovalDetailsFail
    | SoldToActions.LoadActiveSoldTo
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_APPROVED_ORDER_APPROVAL_DETAILS),
      map((action: UserActions.LoadApprovedOrderApprovalDetails) => action.payload),
      filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
      mergeMap(({ userId, sapOrderNumber }) => {
        return this.orderDetailsConnector.getOrderDetails(userId, sapOrderNumber).pipe(
          switchMap((details: OrderDetails) => {
            return [new UserActions.LoadApprovedOrderApprovalDetailsSuccess(details), new SoldToActions.LoadActiveSoldTo(userId)];
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new UserActions.LoadApprovedOrderApprovalDetailsFail({
                sapOrderNumber,
                error: normalizeHttpError(error),
              })
            )
          )
        );
      })
    )
  );

  handleFailureForApprovedOrderApprovalDetails$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderApprovalActionTypes.LOAD_APPROVED_ORDER_APPROVAL_DETAILS_FAIL),
        map((action: UserActions.LoadApprovedOrderApprovalDetailsFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderApproval.loadOrderApprovalDetailsFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  loadOrderApprovalDetails$: Observable<UserActions.LoadOrderApprovalDetailsSuccess | UserActions.LoadOrderApprovalDetailsFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVAL_DETAILS),
        map((action: UserActions.LoadOrderApprovalDetails) => action.payload),
        filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
        mergeMap(({ userId, orderNumber }) => {
          return this.orderApprovalConnector.getOrderApprovalDetails(userId, orderNumber).pipe(
            map((details: OrderApproval) => new UserActions.LoadOrderApprovalDetailsSuccess(details)),
            catchError((error: HttpErrorResponse) =>
              of(
                new UserActions.LoadOrderApprovalDetailsFail({
                  orderNumber,
                  error: normalizeHttpError(error),
                })
              )
            )
          );
        })
      )
    );

  handleFailureForOrderApprovalDetails$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVAL_DETAILS_FAIL),
      map((action: UserActions.LoadOrderApprovalDetailsFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderApproval.loadOrderApprovalDetailsFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  loadOrderApproval$: Observable<
    UserActions.LoadOrderApprovalSuccess | UserActions.LoadOrderApprovalFail | SoldToActions.LoadActiveSoldTo
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVAL),
      map((action: UserActions.LoadOrderApproval) => action.payload),
      filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
      mergeMap(({ userId, orderApprovalCode }) => {
        return this.orderApprovalConnector.get(userId, orderApprovalCode).pipe(
          switchMap((order: Order) => {
            return [new UserActions.LoadOrderApprovalSuccess(order), new SoldToActions.LoadActiveSoldTo(userId)];
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new UserActions.LoadOrderApprovalFail({
                orderApprovalCode,
                error: normalizeHttpError(error),
              })
            )
          )
        );
      })
    )
  );

  handleFailureForLoadOrderApproval$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVAL_FAIL),
      map((action: UserActions.LoadOrderApprovalFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderApproval.loadOrderApprovalFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  loadOrderProcessing$: Observable<UserActions.LoadOrderProcessingSuccess | UserActions.LoadOrderProcessingFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_PROCESSING),
        map((action: UserActions.LoadOrderProcessing) => action.payload),
        filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
        mergeMap(({ userId, orderApprovalCode }) => {
          return this.orderProcessingConnector.getOrderProcessStatus(userId, orderApprovalCode).pipe(
            map((orderProcessStatus: OrderProcessStatus) => {
              return new UserActions.LoadOrderProcessingSuccess(orderProcessStatus, orderApprovalCode);
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                new UserActions.LoadOrderProcessingFail({
                  orderApprovalCode,
                  error: normalizeHttpError(error),
                })
              )
            )
          );
        })
      )
  );

  loadB2BComments$: Observable<UserActions.LoadB2BCommentsSuccess | UserActions.LoadB2BCommentsFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_B2B_COMMENTS),
      map((action: UserActions.LoadB2BComments) => action.payload),
      filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
      mergeMap(({ userId, code, sapOrder }) => {
        return this.orderApprovalConnector.getB2BComments(userId, code, sapOrder).pipe(
          map((comments: Array<B2BComment>) => {
            return new UserActions.LoadB2BCommentsSuccess(comments, code);
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new UserActions.LoadB2BCommentsFail({
                code,
                error: normalizeHttpError(error),
              })
            )
          )
        );
      })
    )
  );

  handleFailureForLoadB2BComments$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_B2B_COMMENTS_FAIL),
      map((action: UserActions.LoadB2BCommentsFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderApproval.loadB2BCommentsFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  loadOrderApprovals$: Observable<UserActions.LoadOrderApprovalsSuccess | UserActions.LoadOrderApprovalsFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVALS),
      filter((action: UserActions.LoadOrderApprovals) => action.userId !== OCC_USER_ID_ANONYMOUS),
      switchMap((action: UserActions.LoadOrderApprovals) =>
        this.orderApprovalConnector.getList(action.userId, action.searchParams).pipe(
          map((orderApprovals: OrderApprovalSearchResult) => {
            return new UserActions.LoadOrderApprovalsSuccess(orderApprovals, action.searchParams);
          }),
          catchError((error: HttpErrorResponse) =>
            of(new UserActions.LoadOrderApprovalsFail(action.searchParams, normalizeHttpError(error)))
          )
        )
      )
    )
  );

  loadOrderApprovalsAdmin$: Observable<UserActions.LoadOrderApprovalsAdminSuccess | UserActions.LoadOrderApprovalsAdminFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVALS_ADMIN),
        filter((action: UserActions.LoadOrderApprovalsAdmin) => action.userId !== OCC_USER_ID_ANONYMOUS),
        switchMap((action: UserActions.LoadOrderApprovalsAdmin) =>
          this.orderApprovalConnector.getAdminList(action.userId, action.searchParams).pipe(
            map(
              (orderApprovals: OrderApprovalSearchResult) =>
                new UserActions.LoadOrderApprovalsAdminSuccess(orderApprovals, action.searchParams)
            ),
            catchError((error: HttpErrorResponse) =>
              of(new UserActions.LoadOrderApprovalsAdminFail(action.searchParams, normalizeHttpError(error)))
            )
          )
        )
      )
    );

  loadAvailableOrderApprovalBuyers$: Observable<
    UserActions.LoadAvailableOrderApprovalsBuyersSuccess | UserActions.LoadAvailableOrderApprovalsBuyersFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.LOAD_AVAILABLE_ORDER_APPROVALS_BUYERS),
      switchMap((action: UserActions.LoadAvailableOrderApprovalsBuyers) => {
        const params = action.searchParams;
        return this.orderApprovalConnector.loadAvailableOrderApprovalBuyers(params, action.userId).pipe(
          map((res) => new UserActions.LoadAvailableOrderApprovalsBuyersSuccess(res, params)),
          catchError((error) => of(new UserActions.LoadAvailableOrderApprovalsBuyersFail(params, normalizeHttpError(error))))
        );
      })
    )
  );

  handleFailureForLoadOrderApprovals$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVALS_FAIL,
        UserActions.OrderApprovalActionTypes.LOAD_ORDER_APPROVALS_ADMIN_FAIL
      ),
      map((action: UserActions.LoadOrderApprovalsFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderApproval.loadOrderApprovalsFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  makeDecision$: Observable<
    | UserActions.OrderApprovalAction
    | CartActions.SetActiveCartId
    | CartActions.LoadTinyCarts
    | CartActions.LoadCart
    | SoldToActions.LoadActiveSoldTo
    | UserActions.UpdateBadgeCountForFeedData
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.MAKE_DECISION),
      map((action: UserActions.MakeDecision) => action.payload),
      filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
      switchMap(({ userId, orderApprovalCode, orderApprovalDecision }) =>
        this.orderApprovalConnector.makeDecision(userId, orderApprovalCode, orderApprovalDecision).pipe(
          switchMap((orderApprovalDecisionData) => {
            const actions: Array<
              | UserActions.OrderApprovalAction
              | CartActions.LoadTinyCarts
              | CartActions.LoadCart
              | SoldToActions.LoadActiveSoldTo
              | UserActions.UpdateBadgeCountForFeedData
            > = [
              new UserActions.MakeDecisionSuccess({
                orderApprovalCode,
                orderApprovalDecision: orderApprovalDecisionData,
              }),
              new UserActions.LoadOrderApprovalSimulationReset(orderApprovalCode),
              new UserActions.LoadB2BComments({ userId, code: orderApprovalCode, sapOrder: false }),
              new UserActions.LoadOrderApproval({ userId, orderApprovalCode }),
              new UserActions.UpdateBadgeCountForFeedData({
                amount: 1,
                badgeType: FeedBadgeType.PENDING_APPROVAL_ORDERS,
                shouldDecrement: true,
              }),
            ];
            if (orderApprovalDecision.decision === OrderApprovalDecisionValue.HIJACK_ORDER) {
              actions.push(new CartActions.LoadTinyCarts(userId));
              actions.push(new CartActions.LoadCart(this.getReloadCurrentActiveStockCartPayload(userId)));
              actions.push(new SoldToActions.LoadActiveSoldTo(userId));
            }
            if (orderApprovalDecision.decision === OrderApprovalDecisionValue.APPROVE) {
              actions.push(new UserActions.LoadOrderProcessing({ userId, orderApprovalCode }));
            }
            return actions;
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new UserActions.MakeDecisionFail({
                orderApprovalCode: orderApprovalCode,
                showErrors: true,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      )
    )
  );

  handleFailureForMakeDecision$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.MAKE_DECISION_FAIL),
      map((action: UserActions.MakeDecisionFail) => action.payload),
      switchMap((payload) => {
        if (payload?.showErrors && !isErrorGloballyHandled(payload?.error?.status)) {
          if (Array.isArray(payload?.error?.details)) {
            const messageActionsArray: GlobalMessageActions.AddMessage[] = payload.error.details
              .filter((errorDetail: ErrorModel) => errorDetail.message)
              .map((errorDetail: ErrorModel) => {
                return new GlobalMessageActions.AddMessage({
                  text: { raw: errorDetail.message },
                  type: GlobalMessageType.MSG_TYPE_ERROR,
                });
              });

            if (messageActionsArray.length) {
              return messageActionsArray;
            }
          }
          return [
            new GlobalMessageActions.AddMessage({
              text: { key: 'orderApproval.makeDecisionFailure_message' },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            }),
          ];
        } else {
          return [new AppActions.NoOpAction()];
        }
      })
    )
  );

  handleSuccessForMakeDecision$: Observable<AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.MAKE_DECISION_SUCCESS),
      map((action: UserActions.MakeDecisionSuccess) => action.payload),
      switchMap((payload) => {
        if (payload.orderApprovalDecision.decision === OrderApprovalDecisionValue.HIJACK_ORDER) {
          //Make sure that we wait for the hijacked cart to be loaded before redirecting the user. Solving errors on cartpage (ESVCX-4763)
          return this.activeCartService.getActiveCart(CartType.stock).pipe(
            filter((cart) => cart.status === OrderStatus.APPROVER_EDITING),
            take(1),
            tap(() =>
              this.routingService.go(
                { cxRoute: 'cart' },
                {
                  queryParams: {
                    cartType: 'stock',
                  },
                }
              )
            ),
            map(() => new AppActions.NoOpAction())
          );
        } else {
          return of(new AppActions.NoOpAction());
        }
      })
    )
  );

  recover$: Observable<
    | UserActions.LoadOrderApprovals
    | UserActions.UpdateBadgeCountForFeedData
    | UserActions.OrderApprovalAction
    | CartActions.TinyCartActions
    | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.RECOVER_ORDER_APPROVAL),
      map((action: UserActions.RecoverOrderApproval) => action.payload),
      switchMap((payload) =>
        this.orderApprovalService.getSearchParameters().pipe(
          take(1),
          map((searchParams) => ({ payload, searchParams }))
        )
      ),
      filter(({ payload }) => payload.userId !== OCC_USER_ID_ANONYMOUS),
      switchMap(({ payload, searchParams }) => {
        return this.orderApprovalConnector.recoverOrder(payload.userId, payload.orderApprovalCode, payload.cartId).pipe(
          switchMap(() => [
            new UserActions.RecoverOrderApprovalSuccess(payload.orderApprovalCode),
            new UserActions.UpdateBadgeCountForFeedData({
              amount: 1,
              badgeType: FeedBadgeType.RETURNED_ORDERS,
              shouldDecrement: true,
            }),
            new CartActions.LoadTinyCarts(payload.userId),
            new CartActions.LoadCart(this.getReloadCurrentActiveStockCartPayload(payload.userId)),
            new UserActions.LoadOrderApprovals(payload.userId, searchParams),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              new UserActions.RecoverOrderApprovalFail({
                orderApprovalCode: payload.orderApprovalCode,
                error: normalizeHttpError(error),
              })
            )
          )
        );
      })
    )
  );

  handleFailureForRecover$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.RECOVER_ORDER_APPROVAL_FAIL),
      map((action: UserActions.RecoverOrderApprovalFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderApproval.recoverFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  handleSuccessForRecover$: Observable<AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.RECOVER_ORDER_APPROVAL_SUCCESS),
      switchMap(() =>
        this.activeCartService.getActiveCart(CartType.stock).pipe(
          // ESVCX-4991:
          // we use skip operator to make sure we will be redirected
          // only when old cart will be patched and new cart (with
          // updated entries and b2b comments from edited approval)
          // will be loaded
          skip(1),
          filter((cart) => cart.status === OrderStatus.RETURNED_TO_USER),
          take(1),
          tap(() =>
            this.routingService.go(
              { cxRoute: 'cart' },
              {
                queryParams: {
                  cartType: 'stock',
                },
              }
            )
          ),
          map(() => new AppActions.NoOpAction())
        )
      )
    )
  );

  abortHijack$: Observable<
    UserActions.OrderApprovalAction | CartActions.TinyCartActions | CartActions.LoadCart | SoldToActions.LoadActiveSoldTo
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.ABORT_ORDER_APPROVAL_HIJACK),
      map((action: UserActions.AbortOrderApprovalHijack) => action.payload),
      filter((payload) => payload.userId !== OCC_USER_ID_ANONYMOUS),
      switchMap(({ userId, cartId, orderApprovalCode }) =>
        this.orderApprovalConnector.abortHijack(userId, cartId).pipe(
          switchMap(() => {
            const actions: Array<
              | UserActions.OrderApprovalAction
              | CartActions.TinyCartActions
              | CartActions.LoadCart
              | SoldToActions.LoadActiveSoldTo
            > = [
              new UserActions.AbortOrderApprovalHijackSuccess(cartId),
              new CartActions.LoadTinyCarts(userId),
              new SoldToActions.LoadActiveSoldTo(userId),
              new CartActions.LoadCart(this.getReloadCurrentActiveStockCartPayload(userId)),
            ];
            if (!!orderApprovalCode) {
              actions.push(new UserActions.LoadOrderApprovalDetails({ userId, orderNumber: orderApprovalCode }));
            }
            return actions;
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              new UserActions.AbortOrderApprovalHijackFail({
                cartId,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      )
    )
  );

  handleFailureForAbortHijack$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.ABORT_ORDER_APPROVAL_HIJACK_FAIL),
      map((action: UserActions.AbortOrderApprovalHijackFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderApproval.abortHijackFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  deleteOrderApproval$: Observable<UserActions.DeleteOrderApprovalFail | UserActions.DeleteOrderApprovalSuccess> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderApprovalActionTypes.DELETE_ORDER_APPROVAL),
        map((action: UserActions.DeleteOrderApproval) => action.payload),
        switchMap(({ userId, orderApprovalCode }) =>
          this.orderApprovalConnector.deleteOrderApproval(userId, orderApprovalCode).pipe(
            map(() => new UserActions.DeleteOrderApprovalSuccess({ userId, orderApprovalCode })),
            catchError((error: HttpErrorResponse) =>
              of(
                new UserActions.DeleteOrderApprovalFail({
                  orderApprovalCode,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        )
      )
  );

  handleFailureForDeleteOrderApproval$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.DELETE_ORDER_APPROVAL_FAIL),
      map((action: UserActions.DeleteOrderApprovalFail) => action.payload),
      map((payload) => {
        if (!isErrorGloballyHandled(payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderApproval.deleteOrderApprovalFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  handleSuccessForDeleteOrderApproval$: Observable<
    GlobalMessageActions.AddMessage | UserActions.LoadOrderApprovals | UserActions.UpdateBadgeCountForFeedData
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderApprovalActionTypes.DELETE_ORDER_APPROVAL_SUCCESS),
      map((action: UserActions.DeleteAccountingCodeAdminSuccess) => action.payload),
      switchMap((payload) =>
        this.orderApprovalService.getSearchParameters().pipe(map((searchParams) => ({ payload, searchParams })))
      ),
      tap(() =>
        this.routingService.go(
          { cxRoute: 'orderHistory' },
          {
            queryParams: {
              tab: OrderStatusTabType.MY_APPROVALS,
            },
          }
        )
      ),
      switchMap(({ payload, searchParams }) => [
        new GlobalMessageActions.AddMessage({
          text: {
            key: 'orderApproval.deleteOrderApprovalSuccess_message',
          },
          type: GlobalMessageType.MSG_TYPE_INFO,
          timeout: 3000,
        }),
        new UserActions.LoadOrderApprovals(payload.userId, searchParams),
        new UserActions.UpdateBadgeCountForFeedData({
          amount: 1,
          badgeType: FeedBadgeType.RETURNED_ORDERS,
          shouldDecrement: true,
        }),
      ])
    )
  );

  constructor(
    private actions$: Actions,
    private orderApprovalConnector: OrderApprovalConnector,
    private orderDetailsConnector: OrderDetailsConnector,
    private orderProcessingConnector: OrderProcessingConnector,
    private orderApprovalService: OrderApprovalFacade,
    private routingService: RoutingService,
    private activeCartService: ActiveCartFacade
  ) {}

  private getReloadCurrentActiveStockCartPayload(userId) {
    return {
      userId,
      cartType: CartType.stock,
      cartId: OCC_CART_ID_CURRENT,
      extraData: {
        active: true,
      },
    };
  }
}
